import React, { FC, Suspense } from 'react';
import { T } from 'src/shared/helpers';
import { productLogicName } from 'src/shared/productNames';
import { getToolInfo, type ResolvedToolCategory } from '../common/constants';
import {
    ItemDot,
    OverviewBody,
    OverviewCard,
    OverviewCardBadge,
    OverviewCardEmpty,
    OverviewCardHeader,
    OverviewCardItem,
    OverviewCardTitle,
    OverviewCategoryTitle,
    OverviewGrid,
    StructureSummaryCard,
    StructureSummaryGrid,
    StructureSummaryLabel,
    StructureSummaryValue
} from '../common/styled';
import type { ProductToolData, SidePanelProduct, ToolDefinition } from '../common/types';
import { useConfiguratorData } from '../common/useConfiguratorData';
import { PanelLinesSkeleton } from '../common/skeletons';

export interface FullScreenOverviewProps {
    product: SidePanelProduct;
    productStructureSummary: { totalVariants: number; maxSides: number; maxPrintAreas: number };
    availableTools: ToolDefinition[];
    orderedCategories: ResolvedToolCategory[];
    toolData: ProductToolData;
    isToolAllowed: (tool: ToolDefinition) => boolean;
}

const ComposerOverview: FC<{ product: SidePanelProduct }> = ({ product }) => {
    const configuratorData = useConfiguratorData(product.id);
    return (
        <>
            <OverviewCategoryTitle>{T._('Scene', 'Admin')}</OverviewCategoryTitle>
            <OverviewCard>
                <OverviewCardHeader>
                    <OverviewCardTitle>{T._('Meshes', 'Admin')}</OverviewCardTitle>
                    <OverviewCardBadge $active={configuratorData.sceneMeshes.length > 0}>{configuratorData.sceneMeshes.length}</OverviewCardBadge>
                </OverviewCardHeader>
                {configuratorData.sceneMeshes.map((mesh, i) => (
                    <OverviewCardItem key={i}>
                        <ItemDot $color='#3b82f6' />
                        <span>
                            {mesh.meshID.substring(0, 8)} ({mesh.sides.length} {mesh.sides.length === 1 ? T._('side', 'Admin') : T._('sides', 'Admin')}
                            {mesh.materials.length > 0 &&
                                `, ${mesh.materials.length} ${mesh.materials.length === 1 ? T._('material', 'Admin') : T._('materials', 'Admin')}`}
                            )
                        </span>
                    </OverviewCardItem>
                ))}
            </OverviewCard>
            <OverviewCategoryTitle>{productLogicName()}</OverviewCategoryTitle>
            <OverviewCard>
                <OverviewCardHeader>
                    <OverviewCardTitle>{T._('Attributes', 'Admin')}</OverviewCardTitle>
                    <OverviewCardBadge $active={configuratorData.attributes.length > 0}>{configuratorData.attributes.length}</OverviewCardBadge>
                </OverviewCardHeader>
                {configuratorData.attributes.map((attr, i) => (
                    <OverviewCardItem key={i}>
                        <ItemDot $color='#3b82f6' />
                        <span>
                            {attr.name} ({attr.options.length} {T._('options', 'Admin')})
                        </span>
                    </OverviewCardItem>
                ))}
            </OverviewCard>
        </>
    );
};

export const FullScreenOverview: FC<FullScreenOverviewProps> = ({
    product,
    productStructureSummary,
    availableTools,
    orderedCategories,
    toolData,
    isToolAllowed
}) => {
    return (
        <OverviewBody>
            <OverviewGrid>
                {product.zakekeEnvironment === 'COMPOSER' ? (
                    <Suspense fallback={null}>
                        <ComposerOverview product={product} />
                    </Suspense>
                ) : (
                    <>
                        <OverviewCategoryTitle>{T._('Product Structure', 'Admin')}</OverviewCategoryTitle>
                        <OverviewCard>
                            <OverviewCardHeader>
                                <OverviewCardTitle>{T._('Product structure', 'Admin')}</OverviewCardTitle>
                            </OverviewCardHeader>
                            <StructureSummaryGrid>
                                <StructureSummaryCard>
                                    {productStructureSummary.totalVariants === 0 ? (
                                        <StructureSummaryLabel>{T._('No variants', 'Admin')}</StructureSummaryLabel>
                                    ) : (
                                        <>
                                            <StructureSummaryValue>{productStructureSummary.totalVariants}</StructureSummaryValue>
                                            <StructureSummaryLabel>
                                                {productStructureSummary.totalVariants === 1 ? T._('Variant', 'Admin') : T._('Variants', 'Admin')}
                                            </StructureSummaryLabel>
                                        </>
                                    )}
                                </StructureSummaryCard>
                                <StructureSummaryCard>
                                    <StructureSummaryValue>{productStructureSummary.maxSides}</StructureSummaryValue>
                                    <StructureSummaryLabel>
                                        {productStructureSummary.maxSides === 1 ? T._('Side', 'Admin') : T._('Sides', 'Admin')}
                                    </StructureSummaryLabel>
                                </StructureSummaryCard>
                                <StructureSummaryCard>
                                    <StructureSummaryValue>{productStructureSummary.maxPrintAreas}</StructureSummaryValue>
                                    <StructureSummaryLabel>
                                        {productStructureSummary.maxPrintAreas === 1 ? T._('Print Area', 'Admin') : T._('Print Areas', 'Admin')}
                                    </StructureSummaryLabel>
                                </StructureSummaryCard>
                            </StructureSummaryGrid>
                        </OverviewCard>
                    </>
                )}
                {orderedCategories.map((category) => {
                    const categoryTools = category.toolKeys
                        .map((toolKey) => availableTools.find((t) => t.key === toolKey))
                        .filter((t): t is ToolDefinition => !!t);
                    if (categoryTools.length === 0) return null;
                    return (
                        <React.Fragment key={category.key}>
                            <OverviewCategoryTitle>{category.label}</OverviewCategoryTitle>
                            {categoryTools.map((tool) => {
                                const info = getToolInfo(product, tool, toolData);
                                const toolAllowed = isToolAllowed(tool);
                                return (
                                    <OverviewCard key={tool.key}>
                                        <OverviewCardHeader>
                                            <OverviewCardTitle>{tool.name}</OverviewCardTitle>
                                            <OverviewCardBadge $active={toolAllowed && info.count > 0}>
                                                {toolAllowed && info.count > 0 ? info.count : '--'}
                                            </OverviewCardBadge>
                                        </OverviewCardHeader>
                                        {!toolAllowed ? (
                                            <OverviewCardEmpty>{T._("You don't have permission to access this tool.", 'Admin')}</OverviewCardEmpty>
                                        ) : !toolData.loaded ? (
                                            <OverviewCardEmpty>
                                                <PanelLinesSkeleton widths={['80%']} lineHeight='14px' gap='0' radius='4px' />
                                            </OverviewCardEmpty>
                                        ) : info.items.length > 0 ? (
                                            info.items.map((item) => (
                                                <OverviewCardItem key={item.id}>
                                                    <ItemDot $color={item.inherited ? '#16a34a' : '#3b82f6'} />
                                                    <span>{item.meta ? `${item.name} — ${item.meta}` : item.name}</span>
                                                </OverviewCardItem>
                                            ))
                                        ) : info.status ? (
                                            <OverviewCardItem>
                                                <ItemDot $color='#10b981' />
                                                <span>{info.status}</span>
                                            </OverviewCardItem>
                                        ) : (
                                            <OverviewCardEmpty>{T._('Not configured', 'Admin')}</OverviewCardEmpty>
                                        )}
                                    </OverviewCard>
                                );
                            })}
                        </React.Fragment>
                    );
                })}
            </OverviewGrid>
        </OverviewBody>
    );
};
